import React, { useState, useEffect, useRef } from "react"
import useFormField from "../../../../hooks/useFormField"
import FormFieldError from "../labels/form-field-error"
import FormInputLabel from "../labels/form-field-label"
import ToggleInfo from "../buttons/toggle-info-button"

export default function SelectionInput({
  name,
  id,
  label,
  options = [],
  placeholder,
  required,
  description,
  validateField,
  globalValue,
  setGlobalValue,
  setError,
  error,
  displayOption,
  closeModalLabel,
  moreInfoLabel,
}) {
  const [selectionOpen, setSelectionOpen] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const initialCheckedValues = options.find((option) => option.checked)?.value || ""
  const { value, isFocused, handleFocus, handleBlur, handleSelectionChange } = useFormField(
    initialCheckedValues,
    validateField,
    globalValue,
    setGlobalValue,
    setError,
  )

  const optionsRef = useRef([])

  const handleToggleDropdown = () => {
    setSelectionOpen((prev) => !prev)
    setFocusedIndex(-1)
  }

  const handleOptionClick = (optionValue) => {
    handleSelectionChange(optionValue)
    setSelectionOpen(false)
    handleBlur({ target: { value: optionValue } })
  }

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setFocusedIndex((prevIndex) => (prevIndex + 1) % options.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setFocusedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : options.length - 1))
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      e.preventDefault()
      handleOptionClick(options[focusedIndex].value)
    } else if (e.key === "Escape") {
      e.preventDefault()
      setSelectionOpen(false)
    }
  }

  useEffect(() => {
    if (selectionOpen && focusedIndex >= 0 && optionsRef.current[focusedIndex]) {
      optionsRef.current[focusedIndex].focus()
    }
  }, [focusedIndex, selectionOpen])

  return (
    <div
      className={`form-element form-element--select__single is-bound Form__Element ${displayOption} ${placeholder || value ? "is-filled" : ""}
       ${required ? "is-required" : ""} ${error ? "is-invalid" : ""} ${isFocused ? "has-focus" : ""}`}
      data-f-element-name={name}
      data-f-type="selection"
    >
      <div className="form-container">
        <div className="form-labels">
          <FormInputLabel id={id} label={label} required={required} />
          <ToggleInfo description={description} id={id} closeModalLabel={closeModalLabel} moreInfoLabel={moreInfoLabel} />
        </div>
        <button
          type="button"
          id={id}
          className="form-input"
          onClick={handleToggleDropdown}
          onFocus={handleFocus}
          onBlur={() => !selectionOpen && handleBlur({ target: { value: value } })}
          onKeyDown={handleKeyDown}
          aria-expanded={selectionOpen}
          aria-required={required}
          aria-label={description + (placeholder != null ? " " + placeholder : "")}
          aria-invalid={!!error}
        >
          {value || placeholder}
          <svg className={`svg-icon svg-icon--arrowdownrounded ${selectionOpen ? "open" : ""}`}>
            <use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref="/dist/svg/symbols.svg#svg-arrowdownrounded">
              &nbsp;
            </use>
          </svg>
        </button>
        {selectionOpen && (
          <div className="options open" role="listbox">
            {options.map((option, index) => (
              <div
                key={index}
                ref={(el) => (optionsRef.current[index] = el)}
                className={`option ${index === focusedIndex ? "focused" : ""}`}
                tabIndex="0"
                role="option"
                aria-selected={value === option.value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleOptionClick(option.value)}
                onKeyDown={handleKeyDown}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      <FormFieldError name={name} error={error} placeholder={placeholder} />
    </div>
  )
}
